// Products list (Unleashed-parity "Products" list — Stuart's ruling 2026-08-26,
// nav.ts: front-and-centre, immediately after Home). One row per product with
// live stock + weighted-average cost, backed by GET /reporting/product-summary
// (services/api/src/modules/reporting/products.ts). Read-only: products are
// created/edited in Settings -> Products (features/settings/components/
// ProductRegister.tsx), never here — mirrors ProductsRegister.tsx's (the
// Reports -> Products card, left untouched) same read-only stance, but with
// the stock/cost columns Unleashed's own "Products" list shows and that
// register never did.
import { useState } from 'react';
import { Badge, Button, Group, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Link } from '@tanstack/react-router';
import { IconPackages, IconSearch } from '@tabler/icons-react';
import { PRODUCT_TYPES, type ProductType } from '@zebra/core';
import { useIsAdmin } from '../../../auth/permissions';
import { EmptyState } from '../../../components/EmptyState';
import { PageHeader } from '../../../components/PageHeader';
import { PRODUCT_TYPE_LABELS } from '../../../lib/enumLabels';
import { useLocations, useProductSummary, useSites } from '../api';
import { useCsvDownload } from '../csv';
import { formatCostPerUom, formatMoneyCents, formatQty } from '../format';

const TYPE_OPTIONS = PRODUCT_TYPES.map((t) => ({ value: t, label: PRODUCT_TYPE_LABELS[t] }));
const STATUS_OPTIONS = [
  { value: 'true', label: 'Active only' },
  { value: 'all', label: 'All (incl. inactive)' },
];

export function ProductsListScreen() {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [type, setType] = useState<ProductType | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [status, setStatus] = useState<'true' | 'all'>('true');

  const isAdmin = useIsAdmin();
  const sitesQuery = useSites();
  const locationsQuery = useLocations({ siteId: siteId ?? undefined });
  const query = useProductSummary({
    q: debouncedSearch.trim() || undefined,
    type: type ?? undefined,
    siteId: siteId ?? undefined,
    locationId: locationId ?? undefined,
    isActive: status,
  });
  const download = useCsvDownload();
  const [csvLoading, setCsvLoading] = useState(false);

  const rows = query.data?.items ?? [];
  const hasFilters = Boolean(debouncedSearch.trim() || type || siteId || locationId || status !== 'true');
  const totalValueCents = rows.reduce((sum, r) => sum + BigInt(r.valueCents), 0n);

  async function handleCsv() {
    setCsvLoading(true);
    try {
      await download(
        '/reporting/product-summary',
        {
          q: debouncedSearch.trim() || undefined,
          type: type ?? undefined,
          siteId: siteId ?? undefined,
          locationId: locationId ?? undefined,
          isActive: status,
        },
        'product-summary.csv',
      );
    } finally {
      setCsvLoading(false);
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Products"
        description="Live stock and weighted-average cost, per product. Product master data (create/edit) lives in Settings -> Products."
        actions={
          <Button variant="light" loading={csvLoading} onClick={handleCsv}>
            Download CSV
          </Button>
        }
      />

      <Group gap="md" wrap="wrap" align="flex-end">
        <TextInput
          label="Search"
          placeholder="SKU or name"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={220}
        />
        <Select
          label="Type"
          placeholder="All types"
          clearable
          data={TYPE_OPTIONS}
          value={type}
          onChange={(v) => setType((v as ProductType) ?? null)}
          w={180}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="Site"
          placeholder="All sites"
          clearable
          data={(sitesQuery.data?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))}
          value={siteId}
          onChange={(v) => {
            setSiteId(v);
            setLocationId(null);
          }}
          w={200}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="Location"
          placeholder="All locations"
          clearable
          data={(locationsQuery.data?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
          value={locationId}
          onChange={setLocationId}
          w={200}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="Status"
          data={STATUS_OPTIONS}
          value={status}
          onChange={(v) => setStatus((v as 'true' | 'all') ?? 'true')}
          w={200}
          comboboxProps={{ withinPortal: true }}
        />
      </Group>

      {query.data && rows.length > 0 && (
        <Text size="sm" c="dimmed">
          Total stock value: <strong>{formatMoneyCents(totalValueCents)}</strong> across {rows.length} product
          {rows.length === 1 ? '' : 's'}.
        </Text>
      )}

      <Table.ScrollContainer minWidth={780}>
        <Table striped highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>SKU</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Unit</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>On hand</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Avg cost</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Stock value</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.productId}>
                <Table.Td>
                  <Link to="/products/$productId" params={{ productId: row.productId }}>
                    {row.sku}
                  </Link>
                </Table.Td>
                <Table.Td>{row.name}</Table.Td>
                <Table.Td>
                  <Badge variant="light">{PRODUCT_TYPE_LABELS[row.type]}</Badge>
                </Table.Td>
                <Table.Td>{row.baseUom}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatQty(row.qty)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCostPerUom(row.avgCostCentsPerUnit, row.baseUom)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatMoneyCents(row.valueCents)}</Table.Td>
                <Table.Td>
                  <Badge color={row.isActive ? 'brandGreen' : 'gray'} variant="light">
                    {row.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
            {!query.isLoading && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <EmptyState
                    icon={IconPackages}
                    title={hasFilters ? 'No products match these filters' : 'No products yet'}
                    hint={
                      hasFilters
                        ? 'Try widening your filters or clearing the search.'
                        : 'Products normally arrive with the Unleashed import at cutover, or are added one at a time in Settings.'
                    }
                    action={
                      isAdmin ? (
                        <Button component={Link} to="/settings/products" variant="light">
                          Go to Settings → Products
                        </Button>
                      ) : undefined
                    }
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
}
